import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

export function FilterBar({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mb-5 flex flex-wrap items-center gap-2.5", className)}>{children}</div>
  );
}

interface FilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce delay in ms; when set, onChange fires after the user stops typing. */
  debounceMs?: number;
  className?: string;
}

/** Rounded search input with leading icon and brand focus ring. */
export function FilterSearch({
  value,
  onChange,
  placeholder = "Szukaj...",
  debounceMs,
  className,
}: FilterSearchProps) {
  // Local mirror so debounced inputs stay responsive while typing.
  const [local, setLocal] = useState(value);

  // Keep the latest onChange in a ref so the debounce timer always calls the
  // current callback without re-arming on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const handleChange = (next: string) => {
    setLocal(next);
    if (debounceMs == null) onChangeRef.current(next);
  };

  useEffect(() => {
    if (debounceMs == null) return;
    const t = setTimeout(() => onChangeRef.current(local), debounceMs);
    return () => clearTimeout(t);
  }, [local, debounceMs]);

  return (
    <div className={cn("relative min-w-[260px] max-w-md flex-1", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-pulso-muted" />
      <input
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-lg border border-black/10 bg-pulso-surface pl-9 pr-3 text-sm outline-none transition-[border-color,box-shadow] placeholder:text-pulso-muted focus:border-pulso-primary focus:ring-4 focus:ring-pulso-primary/15"
      />
    </div>
  );
}
